import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { SectionTitle, KpiCard } from "@/components/dashboard/widgets";
import {
  ArrowLeft, Save, ExternalLink, Copy, Loader2, Eye, MousePointerClick,
  PoundSterling, Percent, TrendingUp, Beaker, Trophy, Plus, Trash2, Store,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

/**
 * Lander Editor + Analytics — Pack E.
 * Edit copy, theme colour, bullets, trust badges, disclosure. Live preview
 * via iframe. Analytics tab shows views/submits/conversion + sub_id breakdown.
 */
export default function LanderEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("design");
  const [days, setDays] = useState(14);
  const [analytics, setAnalytics] = useState(null);
  const [form, setForm] = useState({});
  const [previewKey, setPreviewKey] = useState(0);

  const url = useMemo(() => doc ? `${BACKEND_URL}/l/${doc.slug}` : "", [doc]);

  const load = async () => {
    const r = await api.get(`/publisher/landers/${id}`);
    setDoc(r.data);
    setForm({
      headline:      r.data.headline || "",
      subheadline:   r.data.subheadline || "",
      cta_label:     r.data.cta_label || "",
      hero_eyebrow:  r.data.hero_eyebrow || "",
      theme_primary: r.data.theme?.primary || "#0055FF",
      theme_accent:  r.data.theme?.accent  || "#0F172A",
      bullets:       (r.data.bullets || []).join("\n"),
      trust_badges:  (r.data.trust_badges || []).join("\n"),
      disclosure:    r.data.disclosure || "",
      slug:          r.data.slug || "",
      active:        r.data.active !== false,
      ab_active:     !!r.data.ab_active,
      variants:      Array.isArray(r.data.variants) ? r.data.variants : [],
    });
  };
  useEffect(() => { load().catch(() => toast.error("Could not load lander")); /* eslint-disable-next-line */ }, [id]);

  const loadAnalytics = async () => {
    try {
      const r = await api.get(`/publisher/landers/${id}/analytics?days=${days}`);
      setAnalytics(r.data);
    } catch (e) {
      toast.error("Could not load analytics");
    }
  };
  useEffect(() => { if (tab === "analytics") loadAnalytics(); /* eslint-disable-next-line */ }, [tab, days]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        headline:     form.headline,
        subheadline:  form.subheadline,
        cta_label:    form.cta_label,
        hero_eyebrow: form.hero_eyebrow,
        theme_primary: form.theme_primary,
        theme_accent:  form.theme_accent,
        bullets:      form.bullets.split("\n").map(s => s.trim()).filter(Boolean),
        trust_badges: form.trust_badges.split("\n").map(s => s.trim()).filter(Boolean),
        disclosure:   form.disclosure,
        active:       !!form.active,
      };
      if (form.slug && form.slug !== doc.slug) payload.slug = form.slug;
      payload.ab_active = !!form.ab_active;
      payload.variants  = (form.variants || []).map((v) => ({
        id:            v.id,
        label:         v.label,
        weight:        v.weight,
        headline:      v.headline || undefined,
        subheadline:   v.subheadline || undefined,
        cta_label:     v.cta_label || undefined,
        hero_eyebrow:  v.hero_eyebrow || undefined,
        theme_primary: v.theme_primary || undefined,
      }));
      const r = await api.patch(`/publisher/landers/${id}`, payload);
      setDoc(r.data);
      toast.success("Lander updated");
      setPreviewKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  if (!doc) {
    return <div className="grid place-items-center py-20"><Loader2 className="size-5 text-slate-400 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="lander-editor">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" className="rounded-full" onClick={() => nav("/publisher/landers")}><ArrowLeft className="size-4" /> Landers</Button>
      </div>

      <SectionTitle
        eyebrow="Lander editor"
        title={form.headline || doc.headline}
        subtitle={<>Hosted at <a href={url} target="_blank" rel="noreferrer" className="font-mono text-[12.5px] text-[#0055FF] hover:underline">/l/{doc.slug}</a></>}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" className="rounded-full" onClick={() => copyToClipboard(url, "URL copied")} data-testid="lander-edit-copy"><Copy className="size-4" /> Copy URL</Button>
            <Button variant="outline" className="rounded-full" asChild><a href={url} target="_blank" rel="noreferrer" data-testid="lander-edit-open"><ExternalLink className="size-4" /> Open</a></Button>
            <Button className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" onClick={save} disabled={busy} data-testid="lander-edit-save">
              {busy ? <><Loader2 className="size-4 animate-spin" /> Saving</> : <><Save className="size-4" /> Save</>}
            </Button>
          </div>
        }
      />

      <div className="inline-flex gap-1 bg-white border border-slate-200 rounded-full p-1">
        <TabBtn active={tab === "design"}    onClick={() => setTab("design")}    label="Design" testid="tab-design" />
        <TabBtn active={tab === "variants"}  onClick={() => setTab("variants")}  label="A/B variants" testid="tab-variants" />
        <TabBtn active={tab === "analytics"} onClick={() => setTab("analytics")} label="Analytics" testid="tab-analytics" />
      </div>

      {tab === "variants" && (
        <VariantsTab
          form={form}
          setForm={setForm}
          onSave={save}
          busy={busy}
          url={url}
          landerId={id}
          onPromoted={load}
        />
      )}

      {tab === "design" && (
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6">
          {/* ----- Form column ----- */}
          <div className="space-y-5">
            <Card className="rounded-xl border-slate-200 shadow-soft">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="leading-tight">
                    <div className="text-[11px] uppercase tracking-widest text-[#0055FF] font-semibold">Status</div>
                    <h3 className="font-medium text-slate-900 mt-0.5">{form.active ? "Live" : "Paused"}</h3>
                  </div>
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="lander-active-toggle" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="slug">Slug</Label>
                  <Input id="slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="font-mono text-sm" data-testid="lander-slug" />
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl border-slate-200 shadow-soft">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-medium text-slate-900">Hero copy</h3>
                <Field label="Eyebrow"     v={form.hero_eyebrow} onChange={(v) => setForm({ ...form, hero_eyebrow: v })} data-testid="lander-eyebrow-in" />
                <Field label="Headline"    v={form.headline}     onChange={(v) => setForm({ ...form, headline: v })}     data-testid="lander-headline-in" />
                <FieldArea label="Sub-headline" v={form.subheadline} onChange={(v) => setForm({ ...form, subheadline: v })} data-testid="lander-subheadline-in" />
                <Field label="CTA label"   v={form.cta_label}    onChange={(v) => setForm({ ...form, cta_label: v })}    data-testid="lander-cta-in" />
              </CardContent>
            </Card>

            <Card className="rounded-xl border-slate-200 shadow-soft">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-medium text-slate-900">Theme</h3>
                <div className="grid grid-cols-2 gap-4">
                  <ColorField label="Primary"  v={form.theme_primary} onChange={(v) => setForm({ ...form, theme_primary: v })} testid="lander-primary" />
                  <ColorField label="Accent"   v={form.theme_accent}  onChange={(v) => setForm({ ...form, theme_accent: v })}  testid="lander-accent" />
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl border-slate-200 shadow-soft">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-medium text-slate-900">Trust signals</h3>
                <FieldArea
                  label="Bullets (one per line — max 6)"
                  v={form.bullets} onChange={(v) => setForm({ ...form, bullets: v })}
                  rows={5} data-testid="lander-bullets-in"
                />
                <FieldArea
                  label="Trust badges (one per line — max 6)"
                  v={form.trust_badges} onChange={(v) => setForm({ ...form, trust_badges: v })}
                  rows={4} data-testid="lander-badges-in"
                />
                <FieldArea
                  label="Compliance disclosure (small print, footer)"
                  v={form.disclosure} onChange={(v) => setForm({ ...form, disclosure: v })}
                  rows={4} data-testid="lander-disclosure-in"
                />
              </CardContent>
            </Card>
          </div>

          {/* ----- Preview column ----- */}
          <div className="space-y-3">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Live preview</div>
            <div className="rounded-2xl border border-slate-200 shadow-xl overflow-hidden bg-white" style={{ height: 760 }}>
              <iframe
                key={previewKey}
                src={`${url}?preview=1`}
                title="Lander preview"
                className="w-full h-full"
                data-testid="lander-preview-iframe"
              />
            </div>
            <p className="text-[11.5px] text-slate-500">Tip: changes go live the moment you hit Save — the preview refreshes automatically.</p>
          </div>
        </div>
      )}

      {tab === "analytics" && (
        <AnalyticsTab analytics={analytics} days={days} onDays={setDays} url={url} landerId={id} onPromoted={load} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-1.5 text-[12.5px] rounded-full transition-colors ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
    >
      {label}
    </button>
  );
}

function Field({ label, v, onChange, ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={v || ""} onChange={(e) => onChange(e.target.value)} {...rest} />
    </div>
  );
}
function FieldArea({ label, v, onChange, rows = 3, ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Textarea rows={rows} value={v || ""} onChange={(e) => onChange(e.target.value)} {...rest} />
    </div>
  );
}
function ColorField({ label, v, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={v || "#000000"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-10 w-12 rounded-md border border-slate-200 cursor-pointer"
          data-testid={`${testid}-picker`}
        />
        <Input value={v || ""} onChange={(e) => onChange(e.target.value.toUpperCase())} className="font-mono text-sm" data-testid={`${testid}-hex`} />
      </div>
    </div>
  );
}

function AnalyticsTab({ analytics, days, onDays, url, landerId, onPromoted }) {
  if (!analytics) return <div className="grid place-items-center py-20"><Loader2 className="size-5 text-slate-400 animate-spin" /></div>;
  const t = analytics.totals;
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        {[7, 14, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => onDays(d)}
            className={`px-3 py-1 text-[12px] rounded-full ${days === d ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            data-testid={`analytics-period-${d}`}
          >
            {d}d
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Views"        value={t.views.toLocaleString()}        icon={Eye}             accent="blue"   testid="kpi-views" />
        <KpiCard label="Submissions"  value={t.submits.toLocaleString()}      icon={MousePointerClick} accent="blue" testid="kpi-submits" />
        <KpiCard label="Conv. rate"   value={`${t.conv_pct}%`}                icon={Percent}         accent="emerald" testid="kpi-conv" />
        <KpiCard label="EPC"          value={t.epc_gbp.toFixed(2)} prefix="£" icon={PoundSterling}   accent="emerald" testid="kpi-epc" />
      </div>

      <Card className="rounded-xl border-slate-200 shadow-soft">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp className="size-4 text-[#0055FF]" />
            <h3 className="font-medium text-slate-900">Views vs submissions · last {days} days</h3>
          </div>
          <ResponsiveContainer width="100%" height={300} minHeight={200}>
            <LineChart data={analytics.series} margin={{ left: -8, right: 8, top: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Line type="monotone" dataKey="views"   stroke="#0055FF" strokeWidth={2.2} dot={false} />
              <Line type="monotone" dataKey="submits" stroke="#10B981" strokeWidth={2.2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="rounded-xl border-slate-200 shadow-soft">
        <CardContent className="p-6">
          <h3 className="font-medium text-slate-900 mb-4">Sub-ID breakdown (submissions)</h3>
          {analytics.sub_ids.length === 0 ? (
            <p className="text-sm text-slate-500">
              No tagged traffic yet. Append <code className="text-[12px] text-slate-700">?sub_id=&lt;source&gt;</code> to your URL to track campaigns.<br/>
              Example: <code className="text-[12px] text-slate-700">{url}?sub_id=facebook-q1</code>
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10.5px] uppercase tracking-widest text-slate-500 text-left">
                  <tr><th className="py-2 font-medium">Sub-ID</th><th className="font-medium text-right">Submissions</th></tr>
                </thead>
                <tbody>
                  {analytics.sub_ids.map((s) => (
                    <tr key={s.sub_id} className="border-t border-slate-100" data-testid={`subid-row-${s.sub_id}`}>
                      <td className="py-2 font-mono text-[12.5px] text-slate-700">{s.sub_id}</td>
                      <td className="text-right tabular-nums">{s.submits.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {analytics.variants && analytics.variants.length > 0 && (
        <Card className="rounded-xl border-slate-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <Beaker className="size-4 text-[#0055FF]" />
                <h3 className="font-medium text-slate-900">A/B variants</h3>
                {analytics.ab_active && <span className="text-[10.5px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded font-semibold">Live</span>}
              </div>
            </div>
            <VariantLeaderboard variants={analytics.variants} landerId={landerId} onPromoted={onPromoted} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function VariantLeaderboard({ variants, landerId, onPromoted }) {
  const [busy, setBusy] = React.useState(null);
  // Identify the winner — the variant with the highest conv_pct AND ≥30 views
  const winner = React.useMemo(() => {
    const eligible = variants.filter((v) => v.views >= 30);
    if (eligible.length < 2) return null;
    let best = eligible[0];
    for (const v of eligible) if (v.conv_pct > best.conv_pct) best = v;
    return best;
  }, [variants]);

  const promote = async (vid) => {
    if (!window.confirm("Promote this variant?\n\nThe winning headline/CTA/eyebrow/theme will be copied to the base lander and A/B testing will be switched off. This cannot be undone — but you can always start a new A/B test.")) return;
    setBusy(vid);
    try {
      await api.post(`/publisher/landers/${landerId}/variants/${vid}/promote`);
      toast.success("Variant promoted — base lander updated");
      onPromoted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10.5px] uppercase tracking-widest text-slate-500 text-left">
          <tr>
            <th className="py-2 font-medium">Variant</th>
            <th className="font-medium text-right">Weight</th>
            <th className="font-medium text-right">Views</th>
            <th className="font-medium text-right">Submits</th>
            <th className="font-medium text-right">Conv %</th>
            <th className="font-medium text-right pr-1">Action</th>
          </tr>
        </thead>
        <tbody>
          {variants.map((v) => {
            const isWinner = winner && winner.id === v.id;
            return (
              <tr key={v.id} className="border-t border-slate-100" data-testid={`variant-row-${v.id}`}>
                <td className="py-2 font-medium text-slate-900">
                  <span className="inline-flex items-center gap-1.5">
                    {v.label || v.id}
                    {isWinner && <span className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><Trophy className="size-2.5" /> winner</span>}
                  </span>
                </td>
                <td className="text-right tabular-nums">{v.weight}</td>
                <td className="text-right tabular-nums">{v.views.toLocaleString()}</td>
                <td className="text-right tabular-nums">{v.submits.toLocaleString()}</td>
                <td className={`text-right tabular-nums font-medium ${isWinner ? "text-emerald-700" : "text-slate-700"}`}>{v.conv_pct}%</td>
                <td className="text-right pr-1">
                  <Button
                    size="sm"
                    variant={isWinner ? "default" : "outline"}
                    className={`rounded-full h-7 text-[11px] ${isWinner ? "bg-[#0055FF] hover:bg-[#0044CC] text-white" : ""}`}
                    onClick={() => promote(v.id)}
                    disabled={busy === v.id}
                    data-testid={`variant-promote-${v.id}`}
                  >
                    {busy === v.id ? <Loader2 className="size-3 animate-spin" /> : <Trophy className="size-3" />} Promote
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!winner && variants.length > 1 && (
        <p className="text-[11.5px] text-slate-500 mt-3">A clear winner needs at least 30 views per variant — keep the test running.</p>
      )}
    </div>
  );
}

function VariantsTab({ form, setForm, onSave, busy, url, landerId, onPromoted }) {
  const variants = form.variants || [];

  const addVariant = () => {
    if (variants.length >= 6) { toast.warning("Maximum 6 variants per lander"); return; }
    setForm({
      ...form,
      variants: [...variants, {
        id: `v_${Math.random().toString(36).slice(2, 8)}`,
        label: `Variant ${variants.length + 1}`,
        weight: 1,
        headline: "",
        cta_label: "",
      }],
    });
  };

  const update = (idx, key, value) => {
    const next = variants.slice();
    next[idx] = { ...next[idx], [key]: value };
    setForm({ ...form, variants: next });
  };

  const remove = (idx) => {
    const next = variants.slice();
    next.splice(idx, 1);
    setForm({ ...form, variants: next });
  };

  return (
    <div className="space-y-5" data-testid="variants-tab">
      <Card className="rounded-xl border-slate-200 shadow-soft">
        <CardContent className="p-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="size-10 grid place-items-center rounded-lg bg-[#0055FF]/10 text-[#0055FF]"><Beaker className="size-5" /></span>
            <div>
              <h3 className="font-medium text-slate-900">A/B testing</h3>
              <p className="text-[12.5px] text-slate-500 mt-0.5">Split traffic between {variants.length || 0} variants. Sticky by visitor IP+UA so repeat traffic stays consistent. Submissions stamp the variant onto the lead for honest analytics.</p>
            </div>
          </div>
          <Switch
            checked={!!form.ab_active}
            disabled={variants.length < 2}
            onCheckedChange={(v) => setForm({ ...form, ab_active: v })}
            data-testid="ab-active-toggle"
          />
        </CardContent>
      </Card>

      <Card className="rounded-xl border-slate-200 shadow-soft">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-medium text-slate-900">Variants</h3>
            <Button onClick={addVariant} disabled={variants.length >= 6} variant="outline" className="rounded-full" data-testid="add-variant">
              <Plus className="size-4" /> Add variant
            </Button>
          </div>

          {variants.length === 0 ? (
            <p className="text-sm text-slate-500">Add at least two variants to start an A/B test. Leave fields blank to inherit from the base lander.</p>
          ) : (
            <div className="space-y-3">
              {variants.map((v, idx) => (
                <div key={v.id} className="rounded-lg border border-slate-200 bg-slate-50/40 p-4 space-y-3" data-testid={`variant-edit-${v.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-[11.5px] text-slate-500">{v.id}</span>
                    <Input
                      value={v.label}
                      onChange={(e) => update(idx, "label", e.target.value)}
                      placeholder="Label"
                      className="h-8 text-sm flex-1 min-w-[120px]"
                      data-testid={`variant-label-${v.id}`}
                    />
                    <div className="flex items-center gap-1">
                      <Label className="text-[11.5px] text-slate-500 mr-1">Weight</Label>
                      <Input
                        type="number" min={1} max={100}
                        value={v.weight || 1}
                        onChange={(e) => update(idx, "weight", parseInt(e.target.value || "1"))}
                        className="h-8 w-16 text-sm tabular-nums text-right"
                        data-testid={`variant-weight-${v.id}`}
                      />
                    </div>
                    <Button variant="ghost" size="sm" className="rounded-full text-rose-600 hover:bg-rose-50" onClick={() => remove(idx)} data-testid={`variant-remove-${v.id}`}>
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Headline override" v={v.headline} onChange={(val) => update(idx, "headline", val)} data-testid={`variant-headline-${v.id}`} />
                    <Field label="CTA override"      v={v.cta_label} onChange={(val) => update(idx, "cta_label", val)} data-testid={`variant-cta-${v.id}`} />
                    <Field label="Eyebrow override"  v={v.hero_eyebrow || ""} onChange={(val) => update(idx, "hero_eyebrow", val)} data-testid={`variant-eyebrow-${v.id}`} />
                    <ColorField label="Primary colour override" v={v.theme_primary || ""} onChange={(val) => update(idx, "theme_primary", val)} testid={`variant-colour-${v.id}`} />
                  </div>
                  <FieldArea label="Sub-headline override (optional)" v={v.subheadline || ""} onChange={(val) => update(idx, "subheadline", val)} rows={2} data-testid={`variant-sub-${v.id}`} />
                  <div className="flex items-center justify-end pt-1">
                    <a href={`${url}?vid=${v.id}`} target="_blank" rel="noreferrer" className="text-[12px] text-[#0055FF] hover:underline inline-flex items-center gap-1" data-testid={`variant-preview-${v.id}`}>
                      <Eye className="size-3" /> Preview variant
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-end pt-2">
            <Button className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" onClick={onSave} disabled={busy} data-testid="variants-save">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save variants
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PublishToMarketplaceButton({ landerId, doc }) {
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!name || name.trim().length < 4) { toast.error("Name must be at least 4 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/publisher/landers/${landerId}/publish-template`, { name: name.trim(), description: description.trim() || undefined });
      toast.success("Submitted for review — admin will publish it within 24h");
      setOpen(false); setName(""); setDescription("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="lander-publish-marketplace">
        <Store className="size-4" /> Publish template
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="publish-template-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Store className="size-5 text-[#0055FF]" /> Publish to marketplace</DialogTitle>
            <DialogDescription>
              Snapshot this lander's copy, theme and form fields into a re-usable template. Other publishers will be able to pick it as their starting point — and you earn 10% of the publisher revenue on every accepted lead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Template name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`e.g. ${doc?.sector?.replace("-", " ")} — bold variant`} data-testid="publish-template-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tell other publishers what makes this template convert — best traffic source, target audience, CRO tweaks…" data-testid="publish-template-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="publish-template-submit">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Store className="size-4" />} Submit for review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

